
import React, { useState, useEffect } from 'react';
import { ClassRoom, UserProfile, Message, Announcement, WeeklyLog, StudentConnection, Kudo, ApprovalStatus, AcademicYear, Homework, StudentHomeworkStatus, StuckLog, AttendanceRecord, HomeworkTask, InvitationCode, CalendarEvent, TutorLog, ClassInsight, InvitationType, PreReportedAbsence, Organization, AssessmentEvent, AssessmentResult, AssessmentStatus } from '../types';
import { Header, TabMenu, Avatar, WelcomeBanner, TabMenuItem, YearSelector } from './common';
import { ChatWindow } from './chat';
import { PrincipalHomeworkView } from './homework';
import { StatsView } from './shared';
import { CreateHomeworkModal, InviteParentsModal, CreateEditEventModal, EditProfileModal, PolicyModal } from './modals';
import { HelpModal } from './help';
import { AttendanceTaker, ClassAttendanceRegisterView } from './attendance';
import { KudosLogView, SendKudoModal } from './kudos';
import { SmartCalendar } from './attendance';
import { WeeklyInsightsView } from './insights';
import { useTranslate } from '../hooks/useTranslate';
import { AssessmentManager } from './assessments';
import { isFeatureEnabled, isStaffFeatureEnabled, isParentPolicyEnabled } from '../lib/features';

const ClassDashboard = ({
    cls, user, messages, announcements, logs, students, onUpdateStudentStatus, onSendMessage, onUpdateMessage, onDeleteMessage, setViewingImageUrl, isArchived, academicYear, homeworks, studentHomeworkStatuses, stuckLogs, attendanceRecords, onCreateHomework, onGenerateInvitationCodes, t, allUsers, calendarEvents, onCreateOrUpdateCalendarEvent, onDeleteCalendarEvent, onSendKudo, kudos, tutorLogs, orgName, org, onUpdateHomework, onDeleteHomework, allClasses, classInsights, onSaveClassInsight, onDeleteAccount, onUpdateUser, assessmentEvents, assessmentResults, onSaveAssessmentEvent, onSaveAssessmentResult, invitationCodes = [], onMarkMessagesRead, onTakeAttendance
}: {
    cls: ClassRoom,
    user: UserProfile,
    allUsers: UserProfile[],
    messages: Message[],
    announcements: Announcement[],
    logs: WeeklyLog[],
    students: StudentConnection[],
    kudos: Kudo[],
    onUpdateStudentStatus: (studentId: string, status: ApprovalStatus) => void,
    onSendMessage: (message: Omit<Message, 'id' | 'timestamp' | 'readBy' | 'academicYearId'>) => void,
    onUpdateMessage: (messageId: string, newText: string, targetLanguage: string) => void,
    onDeleteMessage: (messageId: string) => void,
    setViewingImageUrl: (url: string | null) => void,
    isArchived: boolean,
    academicYear: AcademicYear,
    homeworks: Homework[],
    studentHomeworkStatuses: StudentHomeworkStatus[],
    stuckLogs: StuckLog[],
    attendanceRecords: AttendanceRecord[],
    onCreateHomework: (homework: Omit<Homework, 'id' | 'timestamp'>) => void,
    onUpdateHomework: (homeworkId: string, tasks: HomeworkTask[], date: string, attachments?: string[]) => void,
    onDeleteHomework?: (homeworkId: string) => Promise<void>,
    onGenerateInvitationCodes: (count: number) => Promise<InvitationCode[]>,
    calendarEvents: CalendarEvent[];
    onCreateOrUpdateCalendarEvent: (event: Omit<CalendarEvent, 'id'> | CalendarEvent) => Promise<void>;
    onDeleteCalendarEvent: (eventId: string) => Promise<void>;
    onSendKudo: (kudo: Omit<Kudo, 'id' | 'timestamp'>) => Promise<void>;
    t: (key: string, reps?: any) => string,
    tutorLogs: TutorLog[],
    orgName: string,
    org?: Organization,
    allClasses: ClassRoom[],
    classInsights: ClassInsight[],
    onSaveClassInsight: (insight: Omit<ClassInsight, 'id'>) => Promise<void>;
    onDeleteAccount: () => void;
    onUpdateUser: (userId: string, data: Partial<UserProfile>) => void;
    assessmentEvents: AssessmentEvent[];
    assessmentResults: AssessmentResult[];
    onSaveAssessmentEvent: (e: any) => Promise<void>;
    onSaveAssessmentResult: (r: any) => Promise<void>;
    invitationCodes?: InvitationCode[];
    onMarkMessagesRead?: (messageIds: string[]) => void;
    onTakeAttendance?: () => void;
}) => {
    const [activeTab, setActiveTab] = useState<'chat' | 'homework' | 'students' | 'stats' | 'insights' | 'calendar' | 'kudos' | 'assessments'>('chat');
    const [teacherChatMode, setTeacherChatMode] = useState<'group' | 'direct'>('group');
    const [directSearchQuery, setDirectSearchQuery] = useState('');
    const [directFilter, setDirectFilter] = useState<'all' | 'unread' | 'active'>('all');
    const [viewingStudentDM, setViewingStudentDM] = useState<StudentConnection | null>(null);
    const [draftMessage, setDraftMessage] = useState<string>('');
    const [showHomeworkModal, setShowHomeworkModal] = useState(false);
    const [homeworkToEdit, setHomeworkToEdit] = useState<Homework | null>(null);
    const [homeworkPrefill, setHomeworkPrefill] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
    const [isKudoModalOpen, setIsKudoModalOpen] = useState(false);
    const [isEditingProfile, setIsEditingProfile] = useState(false);

    const approvedStudents = students.filter(s => s.approvalStatus === 'approved');
    const pendingStudents = students.filter(s => s.approvalStatus === 'pending');

    // Automatically mark unread direct messages as read when viewing student DM
    useEffect(() => {
        if (!viewingStudentDM || !onMarkMessagesRead) return;
        const unreadStudentMsgs = messages
            .filter(m => m.classId === cls.id && m.studentId === viewingStudentDM.id && m.senderId !== user.id && !(m.readBy || []).includes(user.id))
            .map(m => m.id);
        if (unreadStudentMsgs.length > 0) {
            onMarkMessagesRead(unreadStudentMsgs);
        }
    }, [viewingStudentDM, messages, cls.id, user.id, onMarkMessagesRead]);

    // Automatically mark unread group messages as read when viewing group chat
    useEffect(() => {
        if (activeTab !== 'chat' || teacherChatMode !== 'group' || viewingStudentDM || !onMarkMessagesRead) return;
        const unreadGroupMsgs = messages
            .filter(m => m.classId === cls.id && !m.studentId && m.senderId !== user.id && !(m.readBy || []).includes(user.id))
            .map(m => m.id);
        if (unreadGroupMsgs.length > 0) {
            onMarkMessagesRead(unreadGroupMsgs);
        }
    }, [activeTab, teacherChatMode, viewingStudentDM, messages, cls.id, user.id, onMarkMessagesRead]);

    const handleTeacherSendMessage = (text: string, originalText: string, imageUrl?: string) => {
        if (viewingStudentDM && onMarkMessagesRead) {
            const unreadStudentMsgs = messages
                .filter(m => m.classId === cls.id && m.studentId === viewingStudentDM.id && m.senderId !== user.id && !(m.readBy || []).includes(user.id))
                .map(m => m.id);
            if (unreadStudentMsgs.length > 0) {
                onMarkMessagesRead(unreadStudentMsgs);
            }
        }
        onSendMessage({
            classId: cls.id,
            studentId: viewingStudentDM?.id,
            senderId: user.id,
            senderName: user.name,
            senderRole: 'teacher',
            text,
            originalText,
            originalLanguage: user.language,
            imageUrl
        });
        setDraftMessage('');
    };
    
    const handleDraftMessage = (student: StudentConnection, parent: UserProfile, message: string) => {
        setDraftMessage(message);
        setActiveTab('chat');
        setViewingStudentDM(student);
    };

    const handlePrepareHomework = (description: string) => {
        setHomeworkPrefill(description);
        setHomeworkToEdit(null);
        setActiveTab('homework');
        setShowHomeworkModal(true);
    };

    const handleEditHomework = (homework: Homework) => {
        setHomeworkToEdit(homework);
        setShowHomeworkModal(true);
    };

    const handleEditEvent = (event: CalendarEvent) => {
        setEditingEvent(event);
        setIsEventModalOpen(true);
    };

    const handleDeleteEvent = (eventId: string) => {
        if (window.confirm(t('confirm_delete_event'))) {
            onDeleteCalendarEvent(eventId);
        }
    };

    const teacherTabs: TabMenuItem[] = [];
    if (isStaffFeatureEnabled(org, 'enableTeacherChat', 'chat')) {
        teacherTabs.push({ id: 'chat', label: t('chat'), icon: 'forum' });
    }
    if (isStaffFeatureEnabled(org, 'enableTeacherHomework', 'homework')) {
        teacherTabs.push({ id: 'homework', label: t('homework'), icon: 'home_work' });
    }
    if (isStaffFeatureEnabled(org, 'enableTeacherAssessments', 'assessments')) {
        teacherTabs.push({ id: 'assessments', label: t('assessments'), icon: 'fact_check' });
    }
    teacherTabs.push({ id: 'students', label: t('students'), icon: 'groups' });
    if (isStaffFeatureEnabled(org, 'enableTeacherKudos', 'kudos')) {
        teacherTabs.push({ id: 'kudos', label: t('kudos_log'), icon: 'military_tech' });
    }
    if (isStaffFeatureEnabled(org, 'enableTeacherAttendanceCalendar', 'attendance') || isParentPolicyEnabled(org, 'enableWeeklyCheckin', 'weeklyCheckin')) {
        teacherTabs.push({ id: 'stats', label: t('students_stats'), icon: 'bar_chart' });
    }
    if (isStaffFeatureEnabled(org, 'enableTeacherAttendanceCalendar', 'calendar')) {
        teacherTabs.push({ id: 'calendar', label: t('smart_calendar'), icon: 'event' });
    }
    if (!isArchived && isStaffFeatureEnabled(org, 'enableTeacherInsights', 'insights')) {
        teacherTabs.push({ id: 'insights', label: t('insights'), icon: 'auto_awesome' });
    }

    useEffect(() => {
        if (teacherTabs.length > 0 && !teacherTabs.some(tab => tab.id === activeTab)) {
            setActiveTab(teacherTabs[0].id as any);
        }
    }, [org?.staffPolicies, org?.featureFlags, activeTab]);

    // Direct messages calculations for teacher view
    const totalUnreadDirectCount = messages.filter(m => m.classId === cls.id && m.studentId && m.senderId !== user.id && !m.readBy?.includes(user.id)).length;
    
    // Filtered student conversations for direct inquiry bucket
    const studentConversationList = approvedStudents.map(student => {
        const parent = allUsers.find(u => u.id === student.parentId);
        const studentDirectMsgs = messages.filter(m => m.classId === cls.id && m.studentId === student.id);
        const unreadCount = studentDirectMsgs.filter(m => m.senderId !== user.id && !(m.readBy || []).includes(user.id)).length;
        const lastMsg = studentDirectMsgs.length > 0 ? studentDirectMsgs[studentDirectMsgs.length - 1] : null;
        // Waiting for reply if parent was the last sender (no teacher/principal response yet)
        const isWaitingForReply = Boolean(lastMsg && (lastMsg.senderRole === 'parent' || lastMsg.senderId === student.parentId));
        return {
            student,
            parent,
            unreadCount,
            lastMsg,
            isWaitingForReply,
            hasMessages: studentDirectMsgs.length > 0
        };
    }).filter(item => {
        if (directSearchQuery.trim()) {
            const q = directSearchQuery.toLowerCase();
            const childMatch = item.student.childName.toLowerCase().includes(q);
            const parentMatch = item.parent?.name?.toLowerCase().includes(q) || false;
            if (!childMatch && !parentMatch) return false;
        }
        if (directFilter === 'unread') {
            return item.unreadCount > 0 || item.isWaitingForReply;
        }
        if (directFilter === 'active') {
            return item.hasMessages;
        }
        return true;
    }).sort((a, b) => {
        // First unread, then waiting for reply, then by last message timestamp
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        if (b.unreadCount > 0 && a.unreadCount === 0) return 1;
        if (a.isWaitingForReply && !b.isWaitingForReply) return -1;
        if (!a.isWaitingForReply && b.isWaitingForReply) return 1;
        const timeA = a.lastMsg?.timestamp instanceof Date ? a.lastMsg.timestamp.getTime() : (a.lastMsg?.timestamp ? new Date(a.lastMsg.timestamp).getTime() : 0);
        const timeB = b.lastMsg?.timestamp instanceof Date ? b.lastMsg.timestamp.getTime() : (b.lastMsg?.timestamp ? new Date(b.lastMsg.timestamp).getTime() : 0);
        return timeB - timeA;
    });

    const totalNeedsAttentionCount = approvedStudents.filter(student => {
        const studentDirectMsgs = messages.filter(m => m.classId === cls.id && m.studentId === student.id);
        const unreadCount = studentDirectMsgs.filter(m => m.senderId !== user.id && !(m.readBy || []).includes(user.id)).length;
        const lastMsg = studentDirectMsgs.length > 0 ? studentDirectMsgs[studentDirectMsgs.length - 1] : null;
        const isWaitingForReply = Boolean(lastMsg && (lastMsg.senderRole === 'parent' || lastMsg.senderId === student.parentId));
        return unreadCount > 0 || isWaitingForReply;
    }).length;

    if (viewingStudentDM) {
        const student = students.find(s => s.id === viewingStudentDM.id)!;
        const parent = allUsers.find(u => u.id === student.parentId)!;
        // Direct messages specifically for this student
        const conversationMessages = messages.filter(m => m.classId === cls.id && m.studentId === student.id);
        const isParentDisabled = Boolean(parent?.directChatDisabled || student?.directChatDisabled);
        return (
           <div className="flex flex-col h-full bg-white">
            <Header 
              title={`${student.childName} (${parent?.name || 'Parent'})`}
              subtitle={`${cls.name} • Direct Inquiry`}
              user={user} 
              onBack={() => setViewingStudentDM(null)} 
              onLogout={() => {}} 
              onEditProfile={() => setIsEditingProfile(true)}
              hideProfile={true}
            />
            {isParentDisabled && (
                <div className="bg-amber-50 px-4 py-2 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 font-medium">
                        <span className="material-icons-round text-sm text-amber-600">block</span>
                        Direct messaging is disabled for this parent by the principal.
                    </span>
                    <span className="text-[10px] bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-bold">Restricted</span>
                </div>
            )}
            <div className="flex-1 overflow-hidden">
               <ChatWindow 
                 messages={conversationMessages} 
                 announcements={[]} 
                 onSendMessage={handleTeacherSendMessage}
                 user={user}
                 targetLanguage={parent?.language || 'en'}
                 readOnly={isArchived}
                 allUsers={allUsers}
                 setViewingImageUrl={setViewingImageUrl}
                 initialMessage={draftMessage}
                 kudos={kudos.filter(k => k.studentId === student.id)}
                 onUpdateMessage={(id, text) => onUpdateMessage(id, text, parent?.language || 'en')}
                 onDeleteMessage={onDeleteMessage}
                 students={[student]}
                 isDirectChat={true}
                 allowAttachments={true}
                 onMarkMessagesRead={onMarkMessagesRead}
               />
            </div>
            {isEditingProfile && (
                <EditProfileModal
                    user={user}
                    onClose={() => setIsEditingProfile(false)}
                    onSave={onUpdateUser}
                    t={t}
                    onDeleteAccount={onDeleteAccount}
                />
            )}
          </div>
        )
    }

    return (
      <div className="flex flex-col flex-1 bg-gray-50 overflow-hidden">
        <TabMenu items={teacherTabs} activeTab={activeTab} onSelectTab={(id) => setActiveTab(id as any)} />
        <div className="flex-1 overflow-y-auto">
            {activeTab === 'chat' && (
                 <div className="h-full flex flex-col">
                    {/* Two Bucket Switcher */}
                    <div className="bg-white border-b px-4 py-2.5 flex items-center justify-between gap-3 shadow-xs">
                        <div className="flex bg-gray-100 p-1 rounded-xl w-full max-w-md">
                            <button
                                type="button"
                                onClick={() => setTeacherChatMode('group')}
                                className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                    teacherChatMode === 'group'
                                        ? 'bg-white text-indigo-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-800'
                                }`}
                            >
                                <span className="material-icons-round text-base">campaign</span>
                                <span>Class Broadcasts</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setTeacherChatMode('direct')}
                                className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                    teacherChatMode === 'direct'
                                        ? 'bg-white text-indigo-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-800'
                                }`}
                            >
                                <span className="material-icons-round text-base">forum</span>
                                <span>Direct Inquiries (1:1)</span>
                                {totalUnreadDirectCount > 0 && (
                                    <span className="bg-rose-500 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold animate-pulse">
                                        {totalUnreadDirectCount}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>

                    {teacherChatMode === 'group' ? (
                        <div className="flex-1 overflow-hidden flex flex-col">
                            {/* Horizontal quick student DM strip */}
                            {!isArchived && (
                                <div className="p-2 border-b bg-white">
                                    <div className="flex items-center justify-between mb-1.5 px-2">
                                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Quick Direct Message</p>
                                        <button 
                                            onClick={() => setTeacherChatMode('direct')}
                                            className="text-xs text-indigo-600 font-bold hover:underline flex items-center gap-0.5"
                                        >
                                            <span>View All ({approvedStudents.length})</span>
                                            <span className="material-icons-round text-sm">arrow_forward</span>
                                        </button>
                                    </div>
                                    <div className="flex gap-3 overflow-x-auto pb-1.5 scrollbar-hide">
                                        {approvedStudents.map(student => {
                                            const hasUnread = messages.some(m => m.studentId === student.id && m.senderId !== user.id && !m.readBy.includes(user.id));
                                            return (
                                             <button key={student.id} onClick={() => setViewingStudentDM(student)} className="flex flex-col items-center gap-1 w-16 text-center flex-shrink-0 group">
                                                <Avatar photoUrl={student.photoUrl} name={student.childName} size="md" unread={hasUnread} />
                                                <span className="text-[11px] truncate w-full text-gray-700 font-medium group-hover:text-indigo-600">{student.childName}</span>
                                            </button>
                                        )})}
                                    </div>
                                </div>
                            )}
                            <div className="flex-1 overflow-hidden">
                                <ChatWindow 
                                    messages={messages.filter(m => m.classId === cls.id && !m.studentId)}
                                    announcements={announcements}
                                    onSendMessage={handleTeacherSendMessage}
                                    user={user}
                                    targetLanguage={user.language}
                                    readOnly={isArchived}
                                    allUsers={allUsers}
                                    setViewingImageUrl={setViewingImageUrl}
                                    kudos={kudos.filter(k => 
                                        (k.studentId && approvedStudents.some(s => s.id === k.studentId)) ||
                                        (k.teacherId && cls.teacherIds.includes(k.teacherId))
                                    )}
                                    onUpdateMessage={(id, text) => onUpdateMessage(id, text, user.language)}
                                    onDeleteMessage={onDeleteMessage}
                                    students={students}
                                    isDirectChat={false}
                                    onMarkMessagesRead={onMarkMessagesRead}
                                />
                            </div>
                        </div>
                    ) : (
                        /* Direct Inquiries Management Hub (Handles 40+ parents smoothly) */
                        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-4">
                            {/* Search and Filters */}
                            <div className="bg-white p-3 rounded-2xl shadow-xs border border-gray-100 space-y-3">
                                <div className="relative">
                                    <span className="material-icons-round absolute left-3 top-2.5 text-gray-400 text-lg">search</span>
                                    <input
                                        type="text"
                                        placeholder="Search student or parent name..."
                                        value={directSearchQuery}
                                        onChange={e => setDirectSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium focus:bg-white focus:border-indigo-500 outline-none"
                                    />
                                    {directSearchQuery && (
                                        <button onClick={() => setDirectSearchQuery('')} className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600">
                                            <span className="material-icons-round text-sm">close</span>
                                        </button>
                                    )}
                                </div>
                                <div className="flex gap-2 flex-wrap text-xs">
                                    <button
                                        onClick={() => setDirectFilter('all')}
                                        className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                                            directFilter === 'all'
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        All Students ({approvedStudents.length})
                                    </button>
                                    <button
                                        onClick={() => setDirectFilter('unread')}
                                        className={`px-3 py-1.5 rounded-lg font-bold transition-all flex items-center gap-1.5 ${
                                            directFilter === 'unread'
                                                ? 'bg-rose-600 text-white'
                                                : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                                        }`}
                                    >
                                        <span className="material-icons-round text-xs">mark_chat_unread</span>
                                        <span>Needs Reply / Unread</span>
                                        {totalNeedsAttentionCount > 0 && (
                                            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                                                directFilter === 'unread' ? 'bg-white text-rose-600' : 'bg-rose-600 text-white'
                                            }`}>
                                                {totalNeedsAttentionCount}
                                            </span>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setDirectFilter('active')}
                                        className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                                            directFilter === 'active'
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        Active Conversations
                                    </button>
                                </div>
                            </div>

                            {/* Thread list */}
                            <div className="space-y-2">
                                {studentConversationList.length === 0 ? (
                                    <div className="text-center py-12 bg-white rounded-2xl border border-gray-100 text-gray-500">
                                        <span className="material-icons-round text-4xl text-gray-300 mb-2">forum</span>
                                        <p className="font-bold text-sm">No conversations found</p>
                                        <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filter</p>
                                    </div>
                                ) : (
                                    studentConversationList.map(({ student, parent, unreadCount, lastMsg, isWaitingForReply, hasMessages }) => (
                                        <button
                                            key={student.id}
                                            onClick={() => setViewingStudentDM(student)}
                                            className="w-full text-left bg-white hover:bg-indigo-50/50 p-3.5 rounded-2xl border border-gray-100 shadow-xs transition-all flex items-center gap-3.5 group"
                                        >
                                            <div className="relative flex-shrink-0">
                                                <Avatar photoUrl={student.photoUrl} name={student.childName} size="md" />
                                                {unreadCount > 0 && (
                                                    <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold border-2 border-white shadow-xs animate-bounce">
                                                        {unreadCount}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-0.5">
                                                    <div className="flex items-center gap-2 truncate">
                                                        <span className="font-bold text-sm text-gray-900 group-hover:text-indigo-600 transition-colors truncate">
                                                            {student.childName}
                                                        </span>
                                                        <span className="text-xs text-gray-400 font-medium truncate">
                                                            • {parent?.name || 'Parent'}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                                                        {unreadCount > 0 ? (
                                                            <span className="bg-rose-100 text-rose-700 font-bold px-2 py-0.5 rounded-md text-[10px] border border-rose-200">
                                                                Unread ({unreadCount})
                                                            </span>
                                                        ) : isWaitingForReply ? (
                                                            <span className="bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-md text-[10px] border border-amber-200">
                                                                Needs Reply
                                                            </span>
                                                        ) : hasMessages ? (
                                                            <span className="bg-emerald-50 text-emerald-700 font-medium px-2 py-0.5 rounded-md text-[10px]">
                                                                Replied
                                                            </span>
                                                        ) : null}
                                                        {lastMsg?.timestamp && (
                                                            <span className="text-[11px] text-gray-400">
                                                                {new Date(lastMsg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between text-xs text-gray-500">
                                                    <p className="truncate max-w-[80%]">
                                                        {lastMsg ? (
                                                            <span>
                                                                <span className="font-semibold text-gray-700">{lastMsg.senderName.split(' ')[0]}: </span>
                                                                {lastMsg.text || 'Photo attachment'}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 italic">No direct messages yet</span>
                                                        )}
                                                    </p>
                                                    <span className="material-icons-round text-gray-300 text-sm group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all">
                                                        chevron_right
                                                    </span>
                                                </div>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'homework' && (
                <div className="p-4 space-y-4">
                    {!isArchived && (
                         <button onClick={() => { setHomeworkPrefill(''); setHomeworkToEdit(null); setShowHomeworkModal(true); }} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2">
                             <span className="material-icons-round">note_add</span>
                             {t('assign_homework')}
                         </button>
                    )}
                    
                    {homeworks.filter(h => h.classId === cls.id).length === 0 && <p className="text-center text-gray-500 py-4">No homework assigned yet.</p>}

                    {homeworks.filter(h => h.classId === cls.id).sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(hw => (
                        <div key={hw.id} className="relative">
                            <PrincipalHomeworkView 
                                classStudents={approvedStudents}
                                allHomeworks={[hw]} // Pass single homework to reuse the view logic
                                studentHomeworkStatuses={studentHomeworkStatuses}
                                stuckLogs={stuckLogs}
                                t={t}
                                tutorLogs={tutorLogs}
                            />
                            {!isArchived && hw.date >= new Date().toISOString().split('T')[0] && (
                                <button 
                                    onClick={() => handleEditHomework(hw)}
                                    className="absolute top-6 right-6 bg-white p-2 rounded-full shadow-sm border text-gray-500 hover:text-indigo-600"
                                    title="Edit Homework"
                                >
                                    <span className="material-icons-round text-sm">edit</span>
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {activeTab === 'assessments' && (
                <AssessmentManager 
                    classId={cls.id}
                    academicYearId={academicYear.id}
                    user={user}
                    events={assessmentEvents.filter(e => e.classId === cls.id)}
                    results={assessmentResults}
                    students={approvedStudents}
                    onSaveEvent={onSaveAssessmentEvent}
                    onSaveResult={onSaveAssessmentResult}
                    onStatusChange={async (id, status, note) => {
                        const evt = assessmentEvents.find(e => e.id === id);
                        if(evt) {
                            await onSaveAssessmentEvent({ ...evt, status, principalNote: note || evt.principalNote });
                        }
                    }}
                    t={t}
                />
            )}
            {activeTab === 'students' && (
                <div className="p-4 space-y-4">
                    {!isArchived && (
                        <div className="grid grid-cols-2 gap-4">
                             <button onClick={() => setShowInviteModal(true)} className="w-full bg-green-600 text-white py-3 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2">
                                 <span className="material-icons-round">person_add</span>
                                 {t('invite_parents')}
                             </button>
                             <button onClick={() => setIsKudoModalOpen(true)} className="w-full bg-yellow-400 text-yellow-900 py-3 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2">
                                 <span className="material-icons-round">military_tech</span>
                                 {t('send_kudo')}
                             </button>
                        </div>
                    )}
                    {pendingStudents.length > 0 && (
                        <div className="bg-white p-4 rounded-xl shadow-sm border">
                            <h3 className="font-bold text-lg">{t('pending_requests')}</h3>
                            {pendingStudents.map(s => (
                                <div key={s.id} className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg mt-2">
                                    <div className="flex items-center gap-2">
                                        <Avatar name={s.childName} photoUrl={s.photoUrl} size="sm" />
                                        <span>{s.childName}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => onUpdateStudentStatus(s.id, 'approved')} className="p-2 bg-green-100 text-green-600 rounded-full"><span className="material-icons-round">done</span></button>
                                        <button onClick={() => onUpdateStudentStatus(s.id, 'rejected')} className="p-2 bg-red-100 text-red-600 rounded-full"><span className="material-icons-round">close</span></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {academicYear ? (
                        <ClassAttendanceRegisterView
                            cls={cls}
                            academicYear={academicYear}
                            students={students}
                            attendanceRecords={attendanceRecords}
                            calendarEvents={calendarEvents}
                            onTakeAttendance={!isArchived ? onTakeAttendance : undefined}
                            t={t}
                        />
                    ) : (
                        <div className="bg-white p-4 rounded-xl shadow-sm border">
                            <h3 className="font-bold text-lg mb-2">{t('students')}</h3>
                            {approvedStudents.map(s => (
                                <div key={s.id} className="flex items-center justify-between p-3 mt-2 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden mr-2">
                                        <Avatar name={s.childName} photoUrl={s.photoUrl} size="sm" />
                                        <span className="truncate font-medium text-gray-700 text-sm">{s.childName}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'kudos' && (
                <KudosLogView 
                    kudos={kudos}
                    students={approvedStudents}
                    t={t}
                />
            )}
            {activeTab === 'stats' && (
                <StatsView students={students} logs={logs} academicYearStartDate={new Date(academicYear.startDate)} t={t} />
            )}
             {activeTab === 'calendar' && (
                <div className="p-4 space-y-4">
                    {!isArchived && (
                         <button onClick={() => { setEditingEvent(null); setIsEventModalOpen(true); }} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2">
                            <span className="material-icons-round">add</span> {t('add_event')}
                         </button>
                    )}
                    <SmartCalendar 
                        user={user}
                        records={[]}
                        events={calendarEvents.filter(e => e.targetClassIds.length === 0 || e.targetClassIds.includes(cls.id))}
                        onEditEvent={handleEditEvent}
                        onDeleteEvent={handleDeleteEvent}
                        t={t}
                        setViewingImageUrl={setViewingImageUrl}
                        userLanguage={user.language}
                    />
                </div>
            )}
            {activeTab === 'insights' && (
                <WeeklyInsightsView 
                    students={approvedStudents}
                    allUsers={allUsers}
                    logs={logs}
                    homeworks={homeworks}
                    statuses={studentHomeworkStatuses}
                    stuckLogs={stuckLogs}
                    attendance={attendanceRecords}
                    onDraftMessage={handleDraftMessage}
                    onPrepareHomework={handlePrepareHomework}
                    t={t}
                    classId={cls.id}
                    teacherId={user.id}
                    existingInsight={classInsights.find(i => i.classId === cls.id)}
                    onSaveInsight={onSaveClassInsight}
                />
            )}
        </div>
        {isKudoModalOpen && (
            <SendKudoModal
                isOpen={isKudoModalOpen}
                onClose={() => setIsKudoModalOpen(false)}
                user={user}
                students={approvedStudents}
                activeYear={academicYear}
                onSendKudo={onSendKudo}
                t={t}
            />
        )}
        {showHomeworkModal && (
            <CreateHomeworkModal 
                onClose={() => setShowHomeworkModal(false)}
                onSave={(tasks: HomeworkTask[], date: string, attachments: string[]) => onCreateHomework({ tasks, date, attachments, classId: cls.id, creatorId: user.id, academicYearId: cls.academicYearId })}
                onUpdate={onUpdateHomework}
                onDelete={onDeleteHomework}
                homeworkToEdit={homeworkToEdit}
                t={t}
                prefill={homeworkPrefill}
                allClassHomeworks={homeworks.filter(h => h.classId === cls.id)}
            />
        )}
        {showInviteModal && (
            <InviteParentsModal
                className={cls.name}
                orgName={orgName}
                org={org}
                classId={cls.id}
                invitationCodes={invitationCodes.filter(c => c.classId === cls.id && c.type === 'parent')}
                onClose={() => setShowInviteModal(false)}
                onGenerate={onGenerateInvitationCodes}
                t={t}
                allUsers={allUsers}
                allStudents={students}
                currentUser={user}
            />
        )}
        {isEventModalOpen && (
             <CreateEditEventModal
                eventToEdit={editingEvent}
                onClose={() => setIsEventModalOpen(false)}
                onSave={onCreateOrUpdateCalendarEvent}
                classes={allClasses} // Passed all classes to allow selecting different ones
                defaultClassId={cls.id}
                user={user}
                academicYear={academicYear}
                t={t}
            />
        )}
      </div>
    );
};

type TeacherManagerProps = {
  user: UserProfile;
  allUsers: UserProfile[];
  classes: ClassRoom[];
  org: Organization;
  allAcademicYears: AcademicYear[];
  activeYear: AcademicYear | undefined;
  allMessages: Message[];
  allLogs: WeeklyLog[];
  allStudents: StudentConnection[];
  allAnnouncements: Announcement[];
  studentHomeworkStatuses: StudentHomeworkStatus[];
  stuckLogs: StuckLog[];
  kudos: Kudo[];
  onUpdateStudentStatus: (studentId: string, status: ApprovalStatus) => void;
  onSendMessage: (message: Omit<Message, 'id' | 'timestamp' | 'readBy'>) => void;
  onUpdateMessage: (messageId: string, newText: string, targetLanguage: string) => void;
  onDeleteMessage: (messageId: string) => void;
  onLogout: () => void;
  onUpdateUser: (userId: string, data: Partial<UserProfile>) => void;
  setViewingImageUrl: (url: string | null) => void;
  allHomeworks: Homework[];
  onCreateHomework: (homework: Omit<Homework, 'id' | 'timestamp'>) => void;
  onUpdateHomework: (homeworkId: string, tasks: HomeworkTask[], date: string, attachments?: string[]) => void;
  onDeleteHomework?: (homeworkId: string) => Promise<void>;
  onGenerateInvitationCodes: (type: InvitationType, count: number, orgId: string, classId?: string) => Promise<InvitationCode[]>;
  attendanceRecords: AttendanceRecord[];
  preReportedAbsences: PreReportedAbsence[];
  onSaveAttendance: (records: Omit<AttendanceRecord, 'id'>[]) => Promise<void>;
  calendarEvents: CalendarEvent[];
  onCreateOrUpdateCalendarEvent: (event: Omit<CalendarEvent, 'id'> | CalendarEvent) => Promise<void>;
  onDeleteCalendarEvent: (eventId: string) => Promise<void>;
  onSendKudo: (kudo: Omit<Kudo, 'id' | 'timestamp'>) => Promise<void>;
  tutorLogs: TutorLog[];
  classInsights: ClassInsight[];
  onSaveClassInsight: (insight: Omit<ClassInsight, 'id'>) => Promise<void>;
  onDeleteAccount: () => void;
  assessmentEvents: AssessmentEvent[];
  assessmentResults: AssessmentResult[];
  onSaveAssessmentEvent: (e: any) => Promise<void>;
  onSaveAssessmentResult: (r: any) => Promise<void>;
  invitationCodes?: InvitationCode[];
  onMarkMessagesRead?: (messageIds: string[]) => void;
};

export const TeacherManager = ({ 
  user, allUsers, classes, org, allAcademicYears, activeYear, allMessages, allLogs, allStudents, allAnnouncements, studentHomeworkStatuses, stuckLogs, kudos, onUpdateStudentStatus, onSendMessage, onUpdateMessage, onDeleteMessage, onLogout, onUpdateUser, setViewingImageUrl, allHomeworks, onCreateHomework, onUpdateHomework, onDeleteHomework, onGenerateInvitationCodes, attendanceRecords, preReportedAbsences, onSaveAttendance, calendarEvents, onCreateOrUpdateCalendarEvent, onDeleteCalendarEvent, onSendKudo, tutorLogs, classInsights, onSaveClassInsight, onDeleteAccount, assessmentEvents, assessmentResults, onSaveAssessmentEvent, onSaveAssessmentResult, invitationCodes = [], onMarkMessagesRead
}: TeacherManagerProps) => {
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [showStaffChat, setShowStaffChat] = useState(false);
  const [showGlobalKudos, setShowGlobalKudos] = useState(false);
  const [isCalendarViewOpen, setIsCalendarViewOpen] = useState(false);
  const [viewingYearId, setViewingYearId] = useState<string | undefined>(activeYear?.id);
  const [takingAttendanceForClass, setTakingAttendanceForClass] = useState<ClassRoom | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  
  const t = useTranslate(user.language);

  // Sync viewingYearId if activeYear becomes available later
  useEffect(() => {
    if (activeYear?.id && !viewingYearId) {
      setViewingYearId(activeYear.id);
    }
  }, [activeYear]);

  const viewingYear = allAcademicYears.find(y => y.id === viewingYearId);
  const myClasses = classes.filter(c => c.teacherIds.includes(user.id) && c.academicYearId === viewingYearId);

  const teacherVisibleYears = allAcademicYears.filter(year => 
    (activeYear && year.id === activeYear.id) || 
    classes.some(c => c.academicYearId === year.id && c.teacherIds.includes(user.id))
  );

  const teacherEvents = calendarEvents.filter(e => 
      e.academicYearId === viewingYearId && (
          e.targetClassIds.length === 0 || // School-wide
          e.targetClassIds.some(id => myClasses.some(c => c.id === id)) || // Target one of my classes
          e.creatorId === user.id // Created by me
      )
  );

  // Filter teacher kudos to display in staff chat
  // Kudos where teacherId is current user ID
  const myTeacherKudos = kudos.filter(k => k.teacherId === user.id);

  if (user.approvalStatus === 'pending') {
    return (
       <div className="h-full flex flex-col items-center justify-center p-8 bg-gray-50 text-center">
          <div className="bg-yellow-100 p-6 rounded-full mb-6 animate-pulse">
            <span className="material-icons-round text-5xl text-yellow-600">hourglass_empty</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('approval_pending')}</h2>
          <p className="text-gray-600 max-w-xs mb-8">
            {t('approval_pending_desc', { orgName: org?.name || 'the organization' })}
          </p>
          <button onClick={onLogout} className="text-indigo-600 font-medium hover:underline cursor-pointer">Logout</button>
       </div>
    );
  }

  if (user.approvalStatus === 'rejected' || user.approvalStatus === 'removed') {
    return (
       <div className="h-full flex flex-col items-center justify-center p-8 bg-gray-50 text-center">
          <span className="material-icons-round text-5xl text-red-400 mb-4">block</span>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('access_denied')}</h2>
          <p className="text-gray-600 max-w-xs mb-8">
            {user.approvalStatus === 'rejected' ? t('access_denied_rejected_desc') : t('access_denied_removed_desc')}
          </p>
          <button onClick={onLogout} className="text-indigo-600 font-medium hover:underline cursor-pointer">Logout</button>
       </div>
    );
  }

    if (takingAttendanceForClass) {
        const todayStr = new Date().toISOString().split('T')[0];
        return (
            <AttendanceTaker
                user={user}
                cls={takingAttendanceForClass}
                students={allStudents.filter(s => s.classId === takingAttendanceForClass.id && s.approvalStatus === 'approved')}
                preReportedAbsences={preReportedAbsences}
                todaysAttendance={attendanceRecords.filter(r => r.classId === takingAttendanceForClass.id && r.date === todayStr)}
                onSave={onSaveAttendance}
                onBack={() => setTakingAttendanceForClass(null)}
                onLogout={onLogout}
                onEditProfile={() => setIsEditingProfile(true)}
                t={t}
                onClose={() => setTakingAttendanceForClass(null)}
            />
        );
    }

    if (showGlobalKudos && viewingYear) {
        const teacherGivenKudos = kudos.filter(k => k.senderId === user.id && k.academicYearId === viewingYearId);
        const teacherReceivedKudos = kudos.filter(k => k.teacherId === user.id && k.academicYearId === viewingYearId);
        const globalKudosList = [...teacherGivenKudos, ...teacherReceivedKudos];
        const globalKudosStudents = allStudents.filter(s => s.academicYearId === viewingYearId);

        return (
            <div className="flex flex-col h-full bg-gray-50">
                <Header 
                    title={t('kudos_log')} 
                    subtitle={viewingYear.name}
                    user={user} 
                    onBack={() => setShowGlobalKudos(false)} 
                    onLogout={onLogout}
                    onEditProfile={() => setIsEditingProfile(true)}
                />
                <div className="flex-1 overflow-y-auto">
                    <KudosLogView 
                        kudos={globalKudosList}
                        students={globalKudosStudents}
                        t={t}
                    />
                </div>
                {isEditingProfile && (
                    <EditProfileModal
                        user={user}
                        onClose={() => setIsEditingProfile(false)}
                        onSave={onUpdateUser}
                        t={t}
                        onDeleteAccount={onDeleteAccount}
                    />
                )}
            </div>
        );
    }

    if (showStaffChat && activeYear) {
        const staffChatId = `STAFF_CHAT_${org?.id}`;
        return (
            <div className="flex flex-col h-full bg-white">
                <Header 
                    title={`${org.name} Staff Room`} 
                    subtitle="Staff Chat"
                    user={user}
                    onBack={() => setShowStaffChat(false)}
                    onLogout={onLogout}
                    onEditProfile={() => setIsEditingProfile(true)}
                />
                <div className="flex-1 overflow-hidden">
                    <ChatWindow 
                        messages={allMessages.filter(m => m.classId === staffChatId && m.academicYearId === activeYear.id)}
                        announcements={[]}
                        kudos={myTeacherKudos}
                        onSendMessage={(text, original, img) => onSendMessage({
                            classId: staffChatId,
                            senderId: user.id,
                            senderName: user.name,
                            senderRole: user.role,
                            text, originalText: original, originalLanguage: user.language, imageUrl: img,
                            academicYearId: activeYear.id
                        })}
                        user={user}
                        targetLanguage={user.language}
                        allUsers={allUsers}
                        setViewingImageUrl={setViewingImageUrl}
                        onUpdateMessage={onUpdateMessage}
                        onDeleteMessage={onDeleteMessage}
                        students={[]}
                    />
                </div>
                {isEditingProfile && (
                    <EditProfileModal
                        user={user}
                        onClose={() => setIsEditingProfile(false)}
                        onSave={onUpdateUser}
                        t={t}
                        onDeleteAccount={onDeleteAccount}
                    />
                )}
            </div>
        );
    }

  if (!selectedClassId) {
    const todayStr = new Date().toISOString().split('T')[0];
    
    return (
      <>
        <div className="flex flex-col h-full bg-gray-50">
          <Header
            title={org.name}
            subtitle={<>{t('teacher')} ({viewingYear?.name || '...'})</>}
            user={user}
            onLogout={onLogout}
            onEditProfile={() => setIsEditingProfile(true)}
            orgLogoUrl={org.photoUrl}
            onShowHelp={() => setShowHelp(true)}
            onShowPolicy={() => setShowPolicy(true)}
          >
             {/* YearSelector removed from here */}
          </Header>
          <WelcomeBanner 
            user={user} 
            /* org={org} removed for teacher dashboard per request */
            children={
                <YearSelector academicYears={teacherVisibleYears} activeYearId={viewingYearId} onSelectYear={setViewingYearId}/>
            }
          />
          <div className="p-4 flex-1 overflow-y-auto">
            {viewingYear && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                    {activeYear && viewingYearId === activeYear.id && isStaffFeatureEnabled(org, 'enableTeacherChat', 'chat') && (
                        <button 
                            onClick={() => setShowStaffChat(true)}
                            className="bg-slate-700 text-white p-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-slate-800 transition-colors"
                        >
                            <span className="material-icons-round">forum</span> {t('staff_chat')}
                        </button>
                    )}
                    {isStaffFeatureEnabled(org, 'enableTeacherKudos', 'kudos') && (
                        <button 
                            onClick={() => setShowGlobalKudos(true)}
                            className={`bg-pink-500 text-white p-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-pink-600 transition-colors ${!(activeYear && viewingYearId === activeYear.id && isStaffFeatureEnabled(org, 'enableTeacherChat', 'chat')) ? 'col-span-2' : ''}`}
                        >
                            <span className="material-icons-round">military_tech</span> {t('kudos_log')}
                        </button>
                    )}
                </div>
            )}
            <h3 className="font-bold text-lg text-gray-800 mb-2 px-1">{t('my_classes')}</h3>
            {myClasses.length === 0 ? (
               <div className="flex flex-col items-center justify-center h-64 text-center opacity-60">
                 <div className="bg-indigo-100 p-6 rounded-full mb-4">
                   <span className="material-icons-round text-5xl text-indigo-500">school</span>
                 </div>
                 <h3 className="text-xl font-bold text-gray-800">{t('no_classes_assigned')}</h3>
                 <p className="text-sm text-gray-500 max-w-xs mt-2">{t('no_classes_assigned_desc')}</p>
               </div>
            ) : (
              <div className="grid gap-4">
                {myClasses.map(cls => {
                  const pendingCount = allStudents.filter(s => s.classId === cls.id && s.approvalStatus === 'pending' && s.academicYearId === viewingYearId).length;
                  const studentCount = allStudents.filter(s => s.classId === cls.id && s.academicYearId === viewingYearId && s.approvalStatus === 'approved').length;
                  const isAttendanceTaken = attendanceRecords.some(r => r.classId === cls.id && r.date === todayStr);

                  return (
                    <div key={cls.id} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 text-left group">
                        <div className="flex justify-between items-start">
                           <button onClick={() => setSelectedClassId(cls.id)} className="flex-1 text-left">
                                <h3 className="font-bold text-lg text-gray-800 group-hover:text-indigo-600">{cls.name}</h3>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-sm text-gray-400">{t('students_count', { count: studentCount.toString() })}</span>
                                    {pendingCount > 0 && activeYear?.id === viewingYearId && (
                                        <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse">
                                            {t('pending_count', { count: pendingCount.toString() })}
                                        </span>
                                    )}
                                </div>
                           </button>
                           {activeYear?.id === viewingYearId && (
                               <button onClick={() => setTakingAttendanceForClass(cls)} className={`flex-shrink-0 px-4 py-2 text-sm font-bold rounded-lg flex items-center gap-1 ${isAttendanceTaken ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                   <span className="material-icons-round text-base">{isAttendanceTaken ? 'edit_calendar' : 'event_available'}</span>
                                   {isAttendanceTaken ? t('edit_attendance') : t('take_attendance')}
                               </button>
                           )}
                        </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {isEditingProfile && (
            <EditProfileModal
                user={user}
                onClose={() => setIsEditingProfile(false)}
                onSave={onUpdateUser}
                t={t}
                onDeleteAccount={onDeleteAccount}
            />
        )}
        {showHelp && (
            <HelpModal role="teacher" onClose={() => setShowHelp(false)} />
        )}
        {showPolicy && (
            <PolicyModal onClose={() => setShowPolicy(false)} />
        )}
        {isCalendarViewOpen && activeYear && (
            <div className="fixed inset-0 bg-black/50" onClick={() => setIsCalendarViewOpen(false)}>Calendar View Stub</div>
        )}
      </>
    );
  }

  const activeClass = classes.find(c => c.id === selectedClassId)!;
  const classAcademicYear = allAcademicYears.find(y => y.id === activeClass.academicYearId)!;
  const classMessages = allMessages.filter(m => m.classId === activeClass.id);
  const classAnnouncements = allAnnouncements.filter(a => a.academicYearId === activeClass.academicYearId && (a.classIds.length === 0 || a.classIds.includes(activeClass.id)));
  const classLogs = allLogs.filter(l => l.classId === activeClass.id);
  const classStudents = allStudents.filter(s => s.classId === activeClass.id);
  const classHomeworks = allHomeworks.filter(h => h.classId === activeClass.id);
  const classStuckLogs = stuckLogs.filter(log => classHomeworks.some(hw => hw.id === log.homeworkId));
  const classAttendance = attendanceRecords.filter(r => r.classId === activeClass.id);
  const classKudos = kudos.filter(k => k.classId === activeClass.id);

  return (
    <div className="flex flex-col h-full bg-white">
      <Header 
        title={activeClass.name} 
        subtitle={<>{t('teacher')}</>} 
        user={user} 
        onBack={() => setSelectedClassId(null)} 
        onLogout={onLogout} 
        onEditProfile={() => setIsEditingProfile(true)}
        hideProfile={true}
      />
      <ClassDashboard
        cls={activeClass}
        user={user}
        allUsers={allUsers}
        messages={classMessages}
        announcements={classAnnouncements}
        logs={classLogs}
        students={classStudents}
        onUpdateStudentStatus={onUpdateStudentStatus}
        onSendMessage={(msg) => onSendMessage({...msg, academicYearId: activeClass.academicYearId})}
        onUpdateMessage={onUpdateMessage}
        onDeleteMessage={onDeleteMessage}
        setViewingImageUrl={setViewingImageUrl}
        isArchived={activeYear?.id !== activeClass.academicYearId}
        academicYear={classAcademicYear}
        homeworks={classHomeworks}
        studentHomeworkStatuses={studentHomeworkStatuses}
        stuckLogs={classStuckLogs}
        attendanceRecords={classAttendance}
        onCreateHomework={onCreateHomework}
        onUpdateHomework={onUpdateHomework}
        onDeleteHomework={onDeleteHomework}
        onGenerateInvitationCodes={(count) => onGenerateInvitationCodes('parent', count, org.id, activeClass.id)}
        calendarEvents={calendarEvents}
        onCreateOrUpdateCalendarEvent={onCreateOrUpdateCalendarEvent}
        onDeleteCalendarEvent={onDeleteCalendarEvent}
        t={t}
        onSendKudo={onSendKudo}
        kudos={kudos}
        tutorLogs={tutorLogs}
        orgName={org.name}
        org={org}
        allClasses={classes} // Pass all classes for event selection
        classInsights={classInsights}
        onSaveClassInsight={onSaveClassInsight}
        onDeleteAccount={onDeleteAccount}
        onUpdateUser={onUpdateUser}
        assessmentEvents={assessmentEvents}
        assessmentResults={assessmentResults}
        onSaveAssessmentEvent={onSaveAssessmentEvent}
        onSaveAssessmentResult={onSaveAssessmentResult}
        invitationCodes={invitationCodes}
        onMarkMessagesRead={onMarkMessagesRead}
        onTakeAttendance={() => setTakingAttendanceForClass(activeClass)}
      />
       {isEditingProfile && (
            <EditProfileModal
                user={user}
                onClose={() => setIsEditingProfile(false)}
                onSave={onUpdateUser}
                t={t}
                onDeleteAccount={onDeleteAccount}
            />
        )}
    </div>
  );
};
